// Filings calendar — upcoming + recent regulatory filings.
//
// Phase 5 reads the cron-trigger schedule from the compliance
// metagraph (mirrored client-side) and computes the next firing
// date for each. A real calendar grid + completed-filings overlay
// lands when a /filings endpoint is added (Phase 6+).

import { CellShell } from './CellShell';

type Filing = {
  id: string;
  label: string;
  cron: string;
  description: string;
};

// Mirrors meta/compliance/global.yaml triggers (cron entries that
// drive filing-draft / board-packet). Hand-maintained mirror; if
// the metagraph adds/removes a filing trigger, mirror the change
// here. Codegen lands with the editor work.
const FILINGS: Filing[] = [
  {
    id: 'nc-29a-quarterly',
    label: 'NC-29A Quarterly',
    cron: '0 9 1 1,4,7,10 *',
    description: 'North Carolina securities quarterly form.',
  },
  {
    id: '1099-annual',
    label: '1099 Annual',
    cron: '0 9 1 1 *',
    description: 'Per-investor 1099s for prior tax year.',
  },
  {
    id: 'k1-annual',
    label: 'K-1 Annual',
    cron: '0 9 1 1 *',
    description: 'Schedule K-1 for partnership / LLC investors.',
  },
  {
    id: 'board-packet-quarterly',
    label: 'Board Packet (Quarterly)',
    cron: '0 9 1 1,4,7,10 *',
    description: 'Quarterly board packet draft.',
  },
];

export function FilingsCalendarCell() {
  const now = new Date();
  const items = FILINGS.map((f) => ({
    ...f,
    nextDue: nextFiringFor(f.cron, now),
  })).sort((a, b) => a.nextDue.getTime() - b.nextDue.getTime());

  return (
    <CellShell
      name="filings-calendar"
      title="Filings"
      purpose="Upcoming + recent regulatory filings."
    >
      <ul className="divide-y divide-border-soft rounded-md border border-border bg-card">
        {items.map((f) => (
          <li key={f.id} className="p-4 flex items-center gap-4">
            <DueChip date={f.nextDue} now={now} />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground">{f.label}</p>
              <p className="text-sm text-foreground-mute">{f.description}</p>
              <p className="text-xs text-foreground-mute font-mono mt-1">cron: {f.cron}</p>
            </div>
          </li>
        ))}
      </ul>
      <p className="text-xs text-foreground-mute">
        Scheduled by Cloud Scheduler against{' '}
        <code>POST /admin/scan-invariants</code> + filing-draft cells.
        Real completion overlay lands with the filings endpoint in Phase 6+.
      </p>
    </CellShell>
  );
}

function DueChip({ date, now }: { date: Date; now: Date }) {
  const days = Math.round((date.getTime() - now.getTime()) / 86400000);
  const tone =
    days < 7 ? 'bg-danger-bg text-danger' : days < 30 ? 'bg-warn-bg text-warn' : 'bg-muted text-foreground';
  return (
    <div className={`shrink-0 px-3 py-2 rounded-md text-center ${tone}`}>
      <div className="text-[10px] uppercase tracking-wide">due</div>
      <div className="font-mono text-xs">{date.toISOString().slice(0, 10)}</div>
      <div className="text-[10px]">{days >= 0 ? `in ${days}d` : `${-days}d ago`}</div>
    </div>
  );
}

// Tiny cron next-firing computer — handles minute, hour, dom, month, dow
// with literal numbers and comma-separated lists. Phase 5's cells
// don't need full cron grammar (the metagraph triggers we mirror are
// a small subset). Step / range / wildcard-`*` are honored.
function nextFiringFor(cron: string, after: Date): Date {
  const [minStr, hourStr, domStr, monStr, dowStr] = cron.split(/\s+/);
  const allowed = (s: string, max: number, min = 0): Set<number> => {
    if (s === '*') {
      const out = new Set<number>();
      for (let i = min; i <= max; i++) out.add(i);
      return out;
    }
    return new Set(s.split(',').map((x) => Number(x)));
  };
  const mins = allowed(minStr!, 59);
  const hours = allowed(hourStr!, 23);
  const doms = allowed(domStr!, 31, 1);
  const mons = allowed(monStr!, 12, 1);
  const dows = allowed(dowStr!, 6); // 0–6, Sunday=0

  // Walk forward minute-by-minute; bounded to 366 days so a
  // pathological cron doesn't loop. UTC matches the projector's
  // cron interpretation.
  const cursor = new Date(Date.UTC(
    after.getUTCFullYear(),
    after.getUTCMonth(),
    after.getUTCDate(),
    after.getUTCHours(),
    after.getUTCMinutes() + 1,
    0,
    0,
  ));
  const cap = 366 * 24 * 60;
  for (let i = 0; i < cap; i++) {
    if (
      mins.has(cursor.getUTCMinutes()) &&
      hours.has(cursor.getUTCHours()) &&
      doms.has(cursor.getUTCDate()) &&
      mons.has(cursor.getUTCMonth() + 1) &&
      dows.has(cursor.getUTCDay())
    ) {
      return cursor;
    }
    cursor.setUTCMinutes(cursor.getUTCMinutes() + 1);
  }
  return cursor;
}
